import { useEffect, useState } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { api } from '../../api/client.js';
import { Loader, Alert, ProgressBar, ProgressRing, Empty } from '../../components/ui.jsx';
import CubMascot from '../../components/CubMascot.jsx';

function LessonCard({ lesson }) {
  const status = lesson.progress?.status ?? 'not_started';
  const done = status === 'completed';
  const started = status === 'in_progress';

  const body = (
    <>
      <div className="lesson-top">
        <span className="lesson-icon" aria-hidden="true">{lesson.icon}</span>
        {done && <span className="pill pill-good">✓ Done</span>}
        {started && <span className="pill pill-warn">In progress</span>}
        {!lesson.unlocked && <span className="pill">🔒 Locked</span>}
      </div>

      <h3>{lesson.title}</h3>
      <p className="muted small">{lesson.subtitle}</p>

      <div className="lesson-meta">
        <span className="pill">⏱ {lesson.estimatedMinutes} min</span>
        <span className="pill">❓ {lesson.quizCount} questions</span>
        <span className="pill">⭐ {lesson.xpReward} XP</span>
      </div>

      {done && lesson.progress && (
        <div className="lesson-score">
          Best score: <strong>{lesson.progress.bestScore}/{lesson.progress.totalQuestions}</strong>
        </div>
      )}

      <span className="lesson-cta">
        {!lesson.unlocked
          ? 'Finish the lesson before this one first'
          : done
            ? 'Play again →'
            : started
              ? 'Continue →'
              : 'Start lesson →'}
      </span>
    </>
  );

  if (!lesson.unlocked) {
    return (
      <div className={`lesson-card is-locked t-${lesson.color}`} aria-disabled="true">
        {body}
      </div>
    );
  }

  return (
    <Link to={`/learn/lesson/${lesson.slug}`} className={`lesson-card t-${lesson.color}`}>
      {body}
    </Link>
  );
}

/** Left-hand list of the course's modules, titles flush left. */
function ModuleSidebar({ modules, selected, onSelect }) {
  return (
    <nav className="module-sidebar" aria-label="Modules">
      <h2 className="module-sidebar-title">Modules</h2>

      <ul className="module-list">
        {modules.map((mod) => {
          const isSelected = mod.slug === selected;
          const locked = mod.unlocked === false;
          const done = mod.completedCount >= mod.lessonCount;

          return (
            <li key={mod.slug}>
              <button
                type="button"
                className={`module-tab t-${mod.color} ${isSelected ? 'is-selected' : ''} ${locked ? 'is-locked' : ''}`}
                onClick={() => onSelect(mod.slug)}
                aria-current={isSelected ? 'true' : undefined}
              >
                <span className="module-tab-icon" aria-hidden="true">{mod.icon}</span>
                <span className="module-tab-text">
                  <span className="module-tab-name">{mod.title}</span>
                  <span className="module-tab-meta">
                    {locked
                      ? '🔒 Locked'
                      : done
                        ? '✓ All done'
                        : `${mod.completedCount} of ${mod.lessonCount} lessons`}
                  </span>
                </span>
                {!locked && (
                  <span className="module-tab-bar" aria-hidden="true">
                    <span style={{ width: `${mod.percent}%` }} />
                  </span>
                )}
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

/**
 * Cub Quests — a module's do-it-for-real to-do list.
 *
 * Not graded and not sent anywhere. A child ticks these off on trust,
 * and the ticks live in this browser only (like the practical flags in
 * lib/practical.js), because the point is the doing, not a record for
 * anyone to mark. Keyed by module slug, so each module keeps its own.
 *
 * Remounted per module via a key at the call site, so the initial read
 * from storage is simply the useState initialiser.
 */
function ModuleMissions({ moduleSlug, missions, color }) {
  const storeKey = `cybercub.missions.${moduleSlug}`;

  const [doneIds, setDoneIds] = useState(() => {
    try {
      const raw = localStorage.getItem(storeKey);
      return new Set(raw ? JSON.parse(raw) : []);
    } catch {
      return new Set();
    }
  });

  const toggle = (id) => {
    setDoneIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      try {
        localStorage.setItem(storeKey, JSON.stringify([...next]));
      } catch {
        /* private window - the ticks just will not persist */
      }
      return next;
    });
  };

  if (!missions || missions.length === 0) return null;

  const done = missions.filter((m) => doneIds.has(m.id)).length;
  const allDone = done === missions.length;

  return (
    <section className={`missions t-${color}`}>
      <div className="missions-head">
        <div>
          <h4>🎯 Cub Quests</h4>
          <p className="small muted">
            Things to actually go and do. Tick them off as you do them — this is just for you.
          </p>
        </div>
        <span className={`missions-count ${allDone ? 'is-done' : ''}`}>
          {allDone ? 'All done! 🎉' : `${done} of ${missions.length}`}
        </span>
      </div>

      <ul className="missions-list">
        {missions.map((m) => {
          const checked = doneIds.has(m.id);
          return (
            <li key={m.id}>
              <label className={`mission ${checked ? 'is-done' : ''}`}>
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggle(m.id)}
                />
                <span className="mission-check" aria-hidden="true">{checked ? '✓' : ''}</span>
                <span className="mission-icon" aria-hidden="true">{m.icon}</span>
                <span className="mission-text">{m.text}</span>
              </label>
            </li>
          );
        })}
      </ul>

      {allDone && (
        <p className="missions-cheer">
          🏆 Every quest done for this module. You did the real thing, not just the reading.
        </p>
      )}
    </section>
  );
}

function ModulePanel({ mod }) {
  return (
    <section className={`module-block t-${mod.color} ${mod.unlocked === false ? 'is-locked' : ''}`}>
      {mod.unlocked === false && (
        <div className="module-lock">
          🔒 <strong>Finish the module above first</strong> — then this one opens up.
        </div>
      )}

      <div className="module-head">
        <div className="grow">
          <div className="row gap-sm wrap">
            <span className="module-icon" aria-hidden="true">{mod.icon}</span>
            <h3 style={{ margin: 0 }}>{mod.title}</h3>
          </div>
          <p className="muted small" style={{ marginTop: '0.5rem' }}>{mod.description}</p>
        </div>
        <ProgressRing value={mod.completedCount} max={mod.lessonCount} tone={mod.color} size={96}>
          <span style={{ fontSize: '1.4rem' }}>{mod.completedCount}</span>
          <span className="small faint" style={{ display: 'block' }}>of {mod.lessonCount}</span>
        </ProgressRing>
      </div>

      <div className="lesson-grid">
        {mod.lessons.map((lesson) => (
          <LessonCard key={lesson.slug} lesson={lesson} />
        ))}
      </div>

      {mod.unlocked !== false && (
        <ModuleMissions
          key={mod.slug}
          moduleSlug={mod.slug}
          missions={mod.missions}
          color={mod.color}
        />
      )}
    </section>
  );
}


/* Rotating tips, keyed to the module being viewed so it changes as the
   child browses rather than flickering on every render. */
const CUB_TIPS = [
  'Three silly words make a stronger password than one clever one.',
  'If a message rushes you, that is the trick. Slow down and check.',
  'Nobody legitimate will ever ask you to read out a one-time code.',
  'The padlock means the connection is private — not that the site is honest.',
  'Before you post a photo, look at everything EXCEPT what you photographed.',
  'A screenshot lasts forever, whatever the app promises.',
  'Updates close holes attackers already know about. Say yes to them.',
  'You are never in trouble for telling a grown-up something felt wrong.',
  'Agree a family code word. It beats every fake voice ever made.',
];

/** Right-hand companion rail: progress, what is next, badges, a tip. */
function CourseRail({ track, done, lessonCount, nextLesson, badges, tipIndex }) {
  const earned = badges.filter((b) => b.awarded_at);
  const xp = track.modules
    .flatMap((m) => m.lessons)
    .reduce((n, l) => n + Number(l.progress?.xpEarned ?? 0), 0);

  return (
    <aside className="course-rail" aria-label="Course progress">
      {/* ---- progress ---- */}
      <section className="rail-card tc">
        <h3 className="rail-title">Course progress</h3>
        <div className="row center">
          <ProgressRing value={done} max={lessonCount || 1} tone="mint" size={116}>
            <span style={{ fontSize: '1.5rem' }}>{done}</span>
            <span className="small faint" style={{ display: 'block' }}>of {lessonCount}</span>
          </ProgressRing>
        </div>
        <div className="rail-stats">
          <div>
            <strong>{xp}</strong>
            <span className="small faint">XP earned</span>
          </div>
          <div>
            <strong>{track.modules.filter((m) => m.completedCount >= m.lessonCount).length}</strong>
            <span className="small faint">modules done</span>
          </div>
        </div>
      </section>

      {/* ---- up next ---- */}
      <section className="rail-card">
        <h3 className="rail-title">Up next</h3>
        {nextLesson ? (
          <>
            <div className="rail-next">
              <span className="rail-next-icon" aria-hidden="true">{nextLesson.icon}</span>
              <div>
                <strong>{nextLesson.title}</strong>
                <span className="small faint">
                  ⏱ {nextLesson.estimatedMinutes} min · ⭐ {nextLesson.xpReward} XP
                </span>
              </div>
            </div>
            <Link to={`/learn/lesson/${nextLesson.slug}`} className="btn" style={{ width: '100%' }}>
              {nextLesson.progress ? 'Continue →' : 'Start →'}
            </Link>
          </>
        ) : (
          <div className="rail-done">
            <span aria-hidden="true">🏆</span>
            <p className="small">
              Every lesson finished. You are a Cyber Hero!
            </p>
          </div>
        )}
      </section>

      {/* ---- badges ---- */}
      <section className="rail-card">
        <div className="row spread gap-sm" style={{ marginBottom: '0.6rem' }}>
          <h3 className="rail-title" style={{ margin: 0 }}>Badges</h3>
          <Link to="/learn/badges" className="rail-link">See all</Link>
        </div>
        {earned.length === 0 ? (
          <p className="small faint" style={{ margin: 0 }}>
            No badges yet — finish a lesson quiz to earn your first.
          </p>
        ) : (
          <div className="rail-badges">
            {earned.slice(0, 6).map((b) => (
              <span key={b.slug} className="rail-badge" title={`${b.title} — ${b.description}`}>
                {b.icon}
              </span>
            ))}
            {earned.length > 6 && <span className="rail-badge is-more">+{earned.length - 6}</span>}
          </div>
        )}
      </section>

      {/* ---- tip ---- */}
      <section className="rail-card rail-tip">
        <CubMascot avatar="cub-sun" mood="star" size={56} />
        <div>
          <strong>Cub says…</strong>
          <p className="small">{CUB_TIPS[tipIndex % CUB_TIPS.length]}</p>
        </div>
      </section>
    </aside>
  );
}

export default function CoursePage() {
  const { trackSlug } = useParams();
  const [data, setData] = useState(null);
  const [dash, setDash] = useState(null);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api.studentDashboard().then(setDash).catch(() => setDash({ badges: [] }));
    api
      .tracks()
      .then((t) => {
        setData(t);
        const track = t.tracks.find((x) => x.slug === trackSlug);
        // Open whichever module they are actually up to.
        const current =
          track?.modules.find((m) => m.unlocked !== false && m.completedCount < m.lessonCount) ??
          track?.modules[0];
        if (current) setSelected(current.slug);
      })
      .catch((err) => setError(err.message));
  }, [trackSlug]);

  if (error) return <Alert kind="bad">{error}</Alert>;
  if (!data || !dash) return <Loader label="Opening your course…" />;

  const track = data.tracks.find((t) => t.slug === trackSlug);
  if (!track) return <Navigate to="/learn" replace />;

  // A course with no modules yet has nothing to show here.
  if (!track.isPublished || track.modules.length === 0) {
    return (
      <>
        <div className="page-head">
          <div>
            <h1>{track.icon} {track.title}</h1>
            <p>{track.tagline}</p>
          </div>
          <Link to="/learn" className="btn btn-ghost">← All courses</Link>
        </div>
        <Empty
          icon="🚧"
          title="This course is still being built"
          text="Come back soon! In the meantime, carry on with Cyber Smart Kids."
          action={<Link to="/learn" className="btn">← Back to my courses</Link>}
        />
      </>
    );
  }

  const lessonCount = track.modules.reduce((n, m) => n + m.lessonCount, 0);
  const done = track.modules.reduce((n, m) => n + m.completedCount, 0);
  const active = track.modules.find((m) => m.slug === selected) ?? track.modules[0];
  const activeIndex = Math.max(0, track.modules.findIndex((m) => m.slug === active?.slug));

  // The first lesson they can actually open that is not finished.
  const nextLesson = track.modules
    .flatMap((m) => m.lessons)
    .find((l) => l.unlocked && l.progress?.status !== 'completed');

  return (
    <>
      <div className="page-head">
        <div>
          <Link to="/learn" className="crumb">← All courses</Link>
          <h1 style={{ marginTop: '0.4rem' }}>{track.icon} {track.title}</h1>
          <p>{track.tagline}</p>
        </div>
        <div className="course-head-progress">
          <ProgressBar
            value={done}
            max={lessonCount || 1}
            tone="mint"
            label={`${done} of ${lessonCount} lessons`}
          />
        </div>
      </div>

      <div className="learn-layout has-rail">
        <ModuleSidebar modules={track.modules} selected={active?.slug} onSelect={setSelected} />
        <div className="module-panel">{active && <ModulePanel mod={active} />}</div>
        <CourseRail
          track={track}
          done={done}
          lessonCount={lessonCount}
          nextLesson={nextLesson}
          badges={dash.badges ?? []}
          tipIndex={activeIndex}
        />
      </div>
    </>
  );
}
